import { ChangeDetectionStrategy, Component, output, input, inject } from "@angular/core";
import { Observable, of, switchMap } from "rxjs";
import { combineLatestWith, distinctUntilChanged, map } from "rxjs/operators";
import { CUSTOMIZED_SIMULATION_AGE_DROPDOWN_VALUE } from "src/app/constants/technical.constants";
import { ClientDataService } from "src/app/services/customer-supplied-data/client-data.service";
import { FmsService } from "src/app/services/fms.service";
import { PensionPlanService } from "src/app/services/pension-plan.service";
import { LoadingStateService } from "src/app/services/loading-state/loading-state.service";
import { StartPayoutAgeService } from "src/app/services/start-payout-age.service";
import { FormModule } from "@storeblocks/form-ng";
import { FormsModule } from "@angular/forms";
import { AsyncPipe } from "@angular/common";
import { YearPipe } from "../../../pipes/year.pipe";
import { EngagementsService } from "src/app/services/engagements.service";
import {
  WithdrawalProfileId,
  WithdrawalProfilesService,
} from "src/app/modules/shared/services/withdrawal-profiles/withdrawal-profiles.service";

const DEFAULT_LABEL = "simulationAge.age.label";

@Component({
  selector: "app-start-payout-age-form-field",
  templateUrl: "./start-payout-age-form-field.component.html",
  styleUrls: ["./start-payout-age-form-field.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [FormModule, FormsModule, AsyncPipe, YearPipe],
})
export class StartPayoutAgeFormFieldComponent {
  private readonly fmsService = inject(FmsService);
  private readonly clientDataService = inject(ClientDataService);
  private readonly loadingStateService = inject(LoadingStateService);
  private readonly pensionPlanService = inject(PensionPlanService);
  private readonly startPayoutAgeService = inject(StartPayoutAgeService);
  private readonly engagementsService = inject(EngagementsService);
  private readonly withdrawalProfilesService = inject(WithdrawalProfilesService);

  public readonly tip = input<string>();
  public readonly selectionChange = output<number>();
  public readonly highlightChange = output<boolean>();

  public CUSTOMIZED_SIMULATION_AGE_DROPDOWN_VALUE = CUSTOMIZED_SIMULATION_AGE_DROPDOWN_VALUE.toString();
  public label$: Observable<string> = this.fmsService.translateAsync(DEFAULT_LABEL);

  public selectedAge$: Observable<string>;
  public startPayoutAgeRange$: Observable<string[]>;
  public isCurrentYearLoaded$: Observable<boolean>;

  constructor() {
    this.startPayoutAgeRange$ = this.startPayoutAgeService.getStartPayoutAgeRange().pipe(
      switchMap((allAges) => {
        return this.engagementsService.startPayoutAgeRangeMinLimit$.pipe(
          map((minLimit) => {
            const startPayoutAgeRange = allAges.filter((age) => age >= minLimit);
            return startPayoutAgeRange.length > 0 ? startPayoutAgeRange : allAges;
          }),
        );
      }),
      map((range) => range.map((i) => i.toString())),
      distinctUntilChanged(),
    );

    this.selectedAge$ = this.startPayoutAgeService.getStartPayoutAge().pipe(
      map(String),
      combineLatestWith(
        this.clientDataService.simulationParametersByEngagementEnable$.pipe(map(({ enable }) => enable)),
      ),
      map(([startPayoutAge, simulationParametersByEngagementEnable]) =>
        simulationParametersByEngagementEnable ? this.CUSTOMIZED_SIMULATION_AGE_DROPDOWN_VALUE : startPayoutAge,
      ),
      switchMap((selectedAge) => {
        return this.startPayoutAgeRange$.pipe(
          switchMap((range) => {
            const firstAge = Number(range[0]);
            if (Number(selectedAge) >= firstAge) {
              return of(selectedAge);
            }
            return this.withdrawalProfilesService.activeWithdrawalProfileId$.pipe(
              map((activeWithdrawalProfileId) => {
                if (activeWithdrawalProfileId !== WithdrawalProfileId.SamePayoutAge) {
                  return selectedAge;
                }

                this.pensionPlanService.changeStartPayoutAge(firstAge);
                return firstAge.toString();
              }),
            );
          }),
        );
      }),
      distinctUntilChanged(),
    );

    this.isCurrentYearLoaded$ = this.loadingStateService.isLoaded$;
  }

  public onPayoutAgeChange(event: Event): void {
    const value = (event.target as HTMLFormElement).value;

    const age = Number(value);
    this.selectionChange.emit(age);
    this.pensionPlanService.changeStartPayoutAge(age);
  }
}
